// Lib
import { flatMap, isEmpty } from 'lodash/fp';

// Utils
import memoizeOne from '../../../../../common/utils/lib/memoizeOne';
import { createSmartSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import { convertUserToMention, getNameAbbreviations } from './mentionUtils';

// Selectors
import { getCurrentUserId, isGuestSelector } from '../../../../user/currentUserSelector';
import {
    currentBoardBlockedUserIdsSelector,
    currentBoardHasPublicEditPermissionsSelector,
    currentBoardHasPublishedFeedbackEnabledSelector,
    currentBoardIsPublishedSelector,
    currentBoardRegisteredSharedUsers,
} from '../../../../utils/permissions/elementPermissionsSelector';
import { getRegisteredContacts } from '../../../../user/contacts/userContactsSelector';
import asBoolMap from '../../../../../common/utils/lib/asBoolMap';
import { getUserId } from '../../../../../common/users/utils/userPropertyUtils';

// Prevent mention suggestions from changing whenever users load (even though we're not editing)
const EMPTY_ARRAY = [];

// NOTE: Currently there's not much point in memoising this as so many operations cause changes to the users map
const getPossibleUserNames = memoizeOne((allUsers) =>
    allUsers ? flatMap(getNameAbbreviations, allUsers.toArray()) : EMPTY_ARRAY,
);

const buildSuggestions = memoizeOne((availableSuggestions, currentUserId, allUsers) => {
    if (!allUsers.size) return EMPTY_ARRAY;

    const possibleUserNames = getPossibleUserNames(allUsers);

    return allUsers.valueSeq().toJS().map(convertUserToMention(possibleUserNames, availableSuggestions));
});

const getNonBlockedUsers = memoizeOne((allUsers, blockedUserIds) => {
    if (isEmpty(blockedUserIds)) return allUsers;

    const blockedUserDictionary = asBoolMap(blockedUserIds);

    return allUsers.filter((user) => !blockedUserDictionary[getUserId(user)]);
});

/**
 * Returns the full list of users that can be mentioned.
 * Filtering is performed within the MilanoteEditor.
 *
 * This selector is designed to only calculate suggestions when required, to improve general performance.
 */
export const getUserMentionSuggestionsWhenEditing = () =>
    createSmartSelector(
        (state, { isEditing }) => isEditing,
        currentBoardRegisteredSharedUsers,
        getCurrentUserId,
        isGuestSelector,
        getRegisteredContacts,
        currentBoardBlockedUserIdsSelector,
        currentBoardHasPublicEditPermissionsSelector,
        currentBoardHasPublishedFeedbackEnabledSelector,
        currentBoardIsPublishedSelector,
        (
            isEditing,
            currentBoardSharedUsers,
            currentUserId,
            isGuest,
            allUsers,
            blockedUserIds,
            currentBoardHasPublicEdit,
            currentBoardHasFeedbackEnabled,
            currentBoardIsPublished,
        ) => {
            if (isGuest || !isEditing) return EMPTY_ARRAY;

            const showAllSuggestions =
                currentBoardHasPublicEdit || (currentBoardIsPublished && currentBoardHasFeedbackEnabled);
            const availableSuggestions = showAllSuggestions
                ? getNonBlockedUsers(allUsers, blockedUserIds)
                : currentBoardSharedUsers;

            return buildSuggestions(availableSuggestions, currentUserId, allUsers);
        },
    );

/**
 * Almost identical to getUserMentionSuggestionsWhenEditing however this will calculate the possible suggestions
 * even when not editing.  Thus this should only be used for components that aren't always displayed, such as
 * the assignment popup.
 */
export const getUserMentionSuggestions = createSmartSelector(
    currentBoardRegisteredSharedUsers,
    getCurrentUserId,
    isGuestSelector,
    getRegisteredContacts,
    currentBoardBlockedUserIdsSelector,
    currentBoardHasPublicEditPermissionsSelector,
    currentBoardHasPublishedFeedbackEnabledSelector,
    currentBoardIsPublishedSelector,
    (
        currentBoardSharedUsers,
        currentUserId,
        isGuest,
        allUsers,
        blockedUserIds,
        currentBoardHasPublicEdit,
        currentBoardHasFeedbackEnabled,
        currentBoardIsPublished,
    ) => {
        if (isGuest) return EMPTY_ARRAY;

        const showAllSuggestions =
            currentBoardHasPublicEdit || (currentBoardIsPublished && currentBoardHasFeedbackEnabled);
        const availableSuggestions = showAllSuggestions
            ? getNonBlockedUsers(allUsers, blockedUserIds)
            : currentBoardSharedUsers;

        return buildSuggestions(availableSuggestions, currentUserId, allUsers);
    },
);
